"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { Pagination } from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";

type TableVariant = "default" | "list";

const TableVariantContext = React.createContext<TableVariant>("default");

function useTableVariant(): TableVariant {
  return React.useContext(TableVariantContext);
}

interface TableProps extends React.ComponentProps<"table"> {
  variant?: TableVariant;
  layout?: "auto" | "fixed";
}

function Table({
  className,
  variant = "default",
  layout,
  style,
  ...props
}: TableProps) {
  const mergedStyle: React.CSSProperties | undefined = layout
    ? { tableLayout: layout, ...style }
    : style;

  return (
    <TableVariantContext.Provider value={variant}>
      <table
        data-slot="table"
        data-variant={variant}
        className={cn(
          "w-full border-collapse caption-bottom text-sm",
          variant === "list" && "min-h-full",
          className,
        )}
        style={mergedStyle}
        {...props}
      />
    </TableVariantContext.Provider>
  );
}

interface TableHeaderProps extends React.ComponentProps<"thead"> {
  sticky?: boolean;
}

function TableHeader({ className, sticky, ...props }: TableHeaderProps) {
  const variant = useTableVariant();
  return (
    <thead
      data-slot="table-header"
      className={cn(
        variant === "default" && "[&_tr]:border-b",
        sticky && "sticky top-0 z-10",
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  const variant = useTableVariant();
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        variant === "default" && "[&_tr:last-child]:border-0",
        variant === "list" && "[&_tr:last-child>td]:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t border-border bg-surface-header font-medium",
        className,
      )}
      {...props}
    />
  );
}

interface TableRowProps extends React.ComponentProps<"tr"> {
  interactive?: boolean;
  rowLabel?: string;
}

function TableRow({
  className,
  interactive,
  rowLabel,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  "aria-label": ariaLabel,
  ...props
}: TableRowProps) {
  const variant = useTableVariant();
  const isInteractive = interactive ?? !!onClick;

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      onKeyDown?.(e);
      if (!isInteractive || e.defaultPrevented) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.(e as unknown as React.MouseEvent<HTMLTableRowElement>);
      }
    },
    [isInteractive, onClick, onKeyDown],
  );

  if (variant === "list") {
    return (
      <tr
        data-slot="table-row"
        role={role ?? (isInteractive ? "button" : undefined)}
        tabIndex={tabIndex ?? (isInteractive ? 0 : undefined)}
        aria-label={ariaLabel ?? rowLabel}
        onClick={onClick}
        onKeyDown={isInteractive ? handleKeyDown : onKeyDown}
        className={cn(
          "bg-surface-elevated transition-colors",
          isInteractive &&
            "group cursor-pointer hover:bg-contrast/[0.02] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-primary",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <tr
      data-slot="table-row"
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={cn(
        "border-b border-white/10 transition-colors data-[state=selected]:bg-contrast/4",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  const variant = useTableVariant();

  if (variant === "list") {
    return (
      <th
        data-slot="table-head"
        scope="col"
        className={cn(
          "bg-surface-header h-[41px] px-page text-left text-sm font-medium text-muted-foreground whitespace-nowrap border-b border-border border-r border-border last:border-r-0",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <th
      data-slot="table-head"
      className={cn(
        "bg-white/1 h-10 px-3 text-left align-middle text-sm font-medium text-muted-foreground whitespace-nowrap border-r border-white/10 last:border-r-0 [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  const variant = useTableVariant();

  if (variant === "list") {
    return (
      <td
        data-slot="table-cell"
        className={cn(
          "h-[68px] px-5 border-b border-border border-r border-border last:border-r-0 last:px-3",
          className,
        )}
        {...props}
      />
    );
  }

  return (
    <td
      data-slot="table-cell"
      className={cn(
        "h-10 px-3 align-middle border-r border-white/10 last:border-r-0 overflow-hidden [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

interface TableFillerRowProps {
  columns: number;
  className?: string;
}

function TableFillerRow({ columns, className }: TableFillerRowProps) {
  return (
    <tr aria-hidden="true" className={className}>
      {Array.from({ length: Math.max(columns - 1, 0) }).map((_, i) => (
        <td key={i} className="border-r border-border" />
      ))}
      {columns > 0 && <td />}
    </tr>
  );
}

/**
 * Per-cell class override. A string applies to every cell; a function receives
 * the column index so callers can mirror real rows that vary by column
 * (widths, borders, the leading column's tint). Defaults to the list cell's
 * `h-[68px]`, so tables with shorter rows must pass their own height.
 */
type SkeletonCellClassName = string | ((columnIndex: number) => string);

function resolveCellClassName(
  cellClassName: SkeletonCellClassName | undefined,
  columnIndex: number,
): string | undefined {
  return typeof cellClassName === "function"
    ? cellClassName(columnIndex)
    : cellClassName;
}

interface TableSkeletonRowProps {
  index?: number;
  cells: React.ReactNode[];
  className?: string;
  cellClassName?: SkeletonCellClassName;
}

function TableSkeletonRow({
  index = 0,
  cells,
  className,
  cellClassName,
}: TableSkeletonRowProps) {
  return (
    <tr
      aria-hidden="true"
      className={cn(
        "bg-surface-elevated motion-reduce:opacity-100 motion-reduce:[animation:none!important]",
        className,
      )}
      style={{
        opacity: 0,
        animation: `skeleton-fade-in 0.4s ease-out ${index * 60}ms forwards`,
      }}
    >
      {cells.map((cell, i) => (
        <TableCell key={i} className={resolveCellClassName(cellClassName, i)}>
          {cell}
        </TableCell>
      ))}
    </tr>
  );
}

interface TableSkeletonBodyProps {
  count: number;
  renderCells: (index: number) => React.ReactNode[];
  className?: string;
  cellClassName?: SkeletonCellClassName;
}

function TableSkeletonBody({
  count,
  renderCells,
  className,
  cellClassName,
}: TableSkeletonBodyProps) {
  return (
    <tbody
      aria-hidden="true"
      className={cn(
        "shimmer shimmer-bg text-foreground motion-reduce:[animation:none!important]",
        className,
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        <TableSkeletonRow
          key={i}
          index={i}
          cells={renderCells(i)}
          cellClassName={cellClassName}
        />
      ))}
    </tbody>
  );
}

interface UseTableSkeletonCountOptions {
  rowHeight?: number;
  headerHeight?: number;
  min?: number;
  /**
   * Whether the measured container is currently mounted/visible. The count is
   * derived from `ref.current.clientHeight`, but when the skeleton container is
   * only rendered while loading (and the loading state turns on *after* an
   * async gate resolves) the element doesn't exist on first render. Threading
   * the loading flag in re-runs the measurement once the container appears, so
   * the count reflects the real viewport instead of staying at `min` (which a
   * `min-h-full` table would then stretch into a few giant rows). Defaults to
   * `true` for callers whose container is always present.
   */
  active?: boolean;
}

const DEFAULT_LIST_ROW_HEIGHT = 68;
const DEFAULT_LIST_HEADER_HEIGHT = 41;
const DEFAULT_MIN_SKELETON_ROWS = 6;

function useTableSkeletonCount(
  ref: React.RefObject<HTMLElement | null>,
  {
    rowHeight = DEFAULT_LIST_ROW_HEIGHT,
    headerHeight = DEFAULT_LIST_HEADER_HEIGHT,
    min = DEFAULT_MIN_SKELETON_ROWS,
    active = true,
  }: UseTableSkeletonCountOptions = {},
) {
  const [count, setCount] = React.useState(min);
  React.useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;

    const measure = () => {
      const h = el.clientHeight;
      if (h <= 0) return;
      setCount(Math.max(min, Math.ceil((h - headerHeight) / rowHeight)));
    };

    measure();

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, rowHeight, headerHeight, min, active]);
  return count;
}

interface TablePaginationProps {
  page: number;
  total: number;
  perPage: number;
  onPageChange: (page: number) => void;
  label?: (total: number) => React.ReactNode;
  disabled?: boolean;
  className?: string;
}

function TablePagination({
  page,
  total,
  perPage,
  onPageChange,
  label,
  disabled,
  className,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1 && total === 0) return null;

  return (
    <div
      className={cn(
        "bg-surface-elevated border-t border-border flex items-center justify-between h-12 px-4 shrink-0",
        className,
      )}
    >
      {label ? (
        <p className="text-sm font-medium text-contrast/70 whitespace-nowrap">
          {label(total)}
        </p>
      ) : (
        <span />
      )}
      <Pagination.Root
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        variant="list"
        disabled={disabled}
      >
        <Pagination.Row>
          <Pagination.First />
          <Pagination.Prev />
          <Pagination.Pages
            strategy="window"
            maxVisible={4}
            windowAlign="center"
          />
          <Pagination.Next />
          <Pagination.Last />
        </Pagination.Row>
      </Pagination.Root>
    </div>
  );
}

interface TableEmptyOverlayProps {
  title: string;
  description?: React.ReactNode;
  icon?: IconName;
  action?: React.ReactNode;
  /** Backdrop skeleton content (typically `<Table variant="list">` with `<TableSkeletonBody />`). */
  backdrop?: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  className?: string;
}

function TableEmptyOverlay({
  title,
  description,
  icon,
  action,
  backdrop,
  containerRef,
  className,
}: TableEmptyOverlayProps) {
  return (
    <div
      ref={containerRef}
      className={cn("flex-1 flex flex-col overflow-hidden relative", className)}
    >
      <div className="flex-1 overflow-hidden bg-surface-primary opacity-[0.3] pointer-events-none">
        {backdrop}
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          {icon && (
            <div
              aria-hidden="true"
              className="flex size-14 items-center justify-center rounded-xl border border-border bg-surface-inset backdrop-blur-sm"
            >
              <Icon
                name={icon}
                size={24}
                className="text-heading opacity-30"
              />
            </div>
          )}
          <div className="flex flex-col items-center gap-1.5">
            <h3 className="text-base font-medium text-heading">{title}</h3>
            {description && (
              <p className="text-sm text-contrast/50">{description}</p>
            )}
          </div>
          {action}
        </div>
      </div>
    </div>
  );
}

TableEmptyOverlay.ClearFiltersButton =
  function TableEmptyOverlayClearFiltersButton({
    onClick,
    children = "Clear all filters",
  }: {
    onClick: () => void;
    children?: React.ReactNode;
  }) {
    return (
      <Button
        type="button"
        onClick={onClick}
        variant="secondary"
        size="sm"
        className="h-8 px-3 hover:bg-contrast/6"
      >
        {children}
      </Button>
    );
  };

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
  TableFillerRow,
  TableSkeletonRow,
  TableSkeletonBody,
  TablePagination,
  TableEmptyOverlay,
  useTableSkeletonCount,
};

export type { TableVariant };
